import type { Catalog, CatalogAsset, StrategyMix } from "./types";
import type { Bucket, Currency, InvestableBucket } from "../types";
import { eqText } from "../excel/fns";
import assetsJson from "./wx-final2/assets.json";
import paramsJson from "./wx-final2/params.json";
import portfoliosJson from "./wx-final2/portfolios.json";

const CATALOGS: Record<string, Catalog> = {
  "wx-final2": {
    id: "wx-final2",
    assets: assetsJson as CatalogAsset[],
    params: paramsJson as Catalog["params"],
    strategyMap: (portfoliosJson as { strategyMap: Catalog["strategyMap"] }).strategyMap,
    portfolios: (portfoliosJson as { portfolios: Catalog["portfolios"] }).portfolios,
    icp: (portfoliosJson as { icp: Catalog["icp"] }).icp,
    notes: (portfoliosJson as { notes: string[] }).notes,
  },
};

export function loadCatalog(id: string): Catalog {
  const c = CATALOGS[id];
  if (!c) throw new Error(`Catálogo desconocido: ${id}`);
  return c;
}

/** Lo que la pestaña Catálogo deriva de la moneda del modelo (B45, B65:C67, E83:F88) y G5:G103 del catálogo de activos. */
export interface CatalogDerived {
  currency: Currency;
  inflation: number;
  bucketNominal: Record<InvestableBucket, number>;
  bucketReal: Record<InvestableBucket, number>;
  mixes: (StrategyMix & { nominal: number; real: number })[];
  /** Rendimiento esperado de una subcategoría en la moneda del modelo ('Catálogo de activos'!G). */
  expectedReturn: (asset: CatalogAsset) => number;
  findAsset: (subcategory: unknown) => CatalogAsset | undefined;
  mixOf: (strategy: unknown) => (StrategyMix & { nominal: number; real: number }) | undefined;
}

export function deriveCatalog(cat: Catalog, currency: Currency): CatalogDerived {
  const p = cat.params;
  const inflation = currency === "USD" ? p.inflation.USD : p.inflation.MXN; // Catálogo!B45
  const dep = p.depreciation;
  const conv = (rMXN: number) => (currency === "USD" ? (1 + rMXN) / (1 + dep) - 1 : rMXN); // B65:B67
  const bucketNominal: Record<InvestableBucket, number> = {
    Preservación: conv(p.bucketReturnsMXN["Preservación"]),
    Ingreso: conv(p.bucketReturnsMXN["Ingreso"]),
    Crecimiento: conv(p.bucketReturnsMXN["Crecimiento"]),
  };
  const bucketReal: Record<InvestableBucket, number> = {
    Preservación: bucketNominal["Preservación"] - inflation,
    Ingreso: bucketNominal["Ingreso"] - inflation,
    Crecimiento: bucketNominal["Crecimiento"] - inflation,
  };
  const mixes = p.mixes.map((m) => ({
    ...m,
    nominal: m.pres * bucketNominal["Preservación"] + m.inc * bucketNominal["Ingreso"] + m.growth * bucketNominal["Crecimiento"], // E83:E88
    real: m.pres * bucketReal["Preservación"] + m.inc * bucketReal["Ingreso"] + m.growth * bucketReal["Crecimiento"], // F83:F88
  }));
  const expectedReturn = (a: CatalogAsset) => {
    // 'Catálogo de activos'!G: =IF($E5=Catálogo!$D$47,$F5,IF(Catálogo!$D$47="USD",(1+$F5)/(1+$B$47)-1,(1+$F5)*(1+$B$47)-1))
    if (a.currency === currency) return a.histReturn;
    return currency === "USD" ? (1 + a.histReturn) / (1 + dep) - 1 : (1 + a.histReturn) * (1 + dep) - 1;
  };
  const bySub = new Map<string, CatalogAsset>();
  for (const a of cat.assets) bySub.set(a.subcategory.trim().toLowerCase(), a);
  return {
    currency,
    inflation,
    bucketNominal,
    bucketReal,
    mixes,
    expectedReturn,
    findAsset: (sub) => (sub === null || sub === undefined || sub === "" ? undefined : bySub.get(String(sub).trim().toLowerCase())),
    mixOf: (strategy) => mixes.find((m) => eqText(m.strategy, strategy)),
  };
}

export const isInvestableBucket = (b: unknown): b is InvestableBucket => b === "Preservación" || b === "Ingreso" || b === "Crecimiento";
export const isBucket = (b: unknown): b is Bucket => isInvestableBucket(b) || b === "Proyectos" || b === "Contribución";

/** Subcategorías de una categoría, en el orden del catálogo (lo que hace el menú dependiente de 3B col C). */
export const assetsByCategory = (cat: Catalog, category: string): CatalogAsset[] => cat.assets.filter((a) => eqText(a.category, category));
